import { memo, useMemo, useRef, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Animated,
  Dimensions,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

import { FL, FONT, display, label, ACCENT_GRADIENT } from "../floodlight";

const WEB = Platform.OS === "web";

// ─── Layout constants ─────────────────────────────────────────────────────────

const CARD_WIDTH       = 300;
const BANNER_HEIGHT    = 68;
const AVATAR_SIZE      = 60;
const AVATAR_OVERLAP   = AVATAR_SIZE / 2;   // how far avatar hangs below banner
const EST_CARD_HEIGHT  = 310;               // used for viewport clamping before layout

// ─── Helpers ─────────────────────────────────────────────────────────────────

function initials(str = "") {
  const parts = str.replace(/\d+$/, "").match(/[A-Z][a-z]*/g) || [str];
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase() || str.slice(0, 2).toUpperCase();
}

function formatJoinedDate(raw) {
  if (!raw) return null;
  try {
    const d = raw.toDate ? raw.toDate() : new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return null;
  }
}

const STATUS_COLOR = {
  online:  FL.pos,
  idle:    FL.amber,
  dnd:     FL.red,
  offline: FL.muted,
};

const STATUS_LABEL = {
  online:  "Online",
  idle:    "Away",
  dnd:     "Do Not Disturb",
  offline: "Offline",
};

// ─── Position calculator ──────────────────────────────────────────────────────

function calcCardPosition(position) {
  if (!position) return { top: 80, left: 80 };

  const { width: sw, height: sh } = Dimensions.get("window");
  const { x, y, width: rw } = position;

  // Prefer to the left of the row; fall back to the right
  let left = x - CARD_WIDTH - 10;
  if (left < 8) left = x + rw + 10;

  // Clamp to screen edges
  left = Math.max(8, Math.min(left, sw - CARD_WIDTH - 8));
  let top  = Math.max(8, Math.min(y - 12, sh - EST_CARD_HEIGHT - 8));

  return { top, left };
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * MemberCard
 *
 * Member profile popup anchored near the clicked row (Floodlight v1.71: a
 * rounded glass card on a soft shadow, a purple-to-blue banner and a round
 * accent avatar).
 *
 * Props:
 *   member    {object|null}  Member data (null = hidden)
 *   position  {{ x, y, width, height }|null}  measureInWindow result of the row
 *   onClose   {function}
 */
const MemberCard = memo(function MemberCard({ member, position, onClose }) {
  const visible = !!member;

  // ── Animation refs ──────────────────────────────────────────────────────────
  const opacity    = useRef(new Animated.Value(0)).current;
  const scale      = useRef(new Animated.Value(0.88)).current;
  const translateY = useRef(new Animated.Value(6)).current;

  // Re-animate whenever the card opens or the target member changes
  useEffect(() => {
    if (!visible) return;

    // Reset before each open so switching members replays the spring
    opacity.setValue(0);
    scale.setValue(0.88);
    translateY.setValue(6);

    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 130,
        useNativeDriver: true,
      }),
      Animated.spring(scale, {
        toValue: 1,
        tension: 260,
        friction: 18,
        useNativeDriver: true,
      }),
      Animated.spring(translateY, {
        toValue: 0,
        tension: 260,
        friction: 18,
        useNativeDriver: true,
      }),
    ]).start();
  }, [visible, member?.id]);

  // ── Derived display values ──────────────────────────────────────────────────
  const abbr        = useMemo(() => member ? initials(member.name) : "", [member?.name]);
  const joined      = useMemo(() => member ? formatJoinedDate(member.joinedAt) : null, [member?.joinedAt]);
  const statusColor = STATUS_COLOR[member?.status] ?? STATUS_COLOR.offline;
  const statusLabel = STATUS_LABEL[member?.status] ?? "Offline";
  const showHandle  = !!(member?.username && member.username !== member.name);
  const cardPos     = useMemo(() => calcCardPosition(position), [position]);

  if (!member) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent={Platform.OS === "android"}
    >
      {/* Invisible full-screen dismiss layer */}
      <TouchableOpacity
        style={StyleSheet.absoluteFillObject}
        activeOpacity={1}
        onPress={onClose}
        accessibilityLabel="Close member card"
      />

      <Animated.View
        style={[
          styles.card,
          cardPos,
          { opacity, transform: [{ scale }, { translateY }] },
        ]}
        // Claim touches so they don't fall through to the dismiss layer
        onStartShouldSetResponder={() => true}
      >
        <View style={styles.banner}>
          <View style={styles.bannerBar} />
          <TouchableOpacity style={styles.closeBtn} onPress={onClose} hitSlop={10} accessibilityRole="button" accessibilityLabel="Close">
            <Ionicons name="close" size={16} color={FL.ink} />
          </TouchableOpacity>
        </View>

        <View style={styles.avatarRow}>
          <View style={styles.avatarOuter}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>{abbr}</Text>
            </View>
            <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
          </View>
        </View>

        <View style={styles.body}>
          <Text style={styles.displayName} numberOfLines={1}>
            {member.name}
          </Text>
          {showHandle && (
            <Text style={styles.handle} numberOfLines={1}>
              @{member.username}
            </Text>
          )}

          <View style={styles.statusRow}>
            <View style={[styles.statusPip, { backgroundColor: statusColor }]} />
            <Text style={styles.statusText}>{statusLabel}</Text>
          </View>

          <View style={styles.divider} />

          <View style={styles.infoRow}>
            <Ionicons name="calendar-outline" size={14} color={FL.greenInk} style={styles.infoIcon} />
            <View>
              <Text style={styles.infoLabel}>Member since</Text>
              <Text style={styles.infoValue}>{joined ?? "—"}</Text>
            </View>
          </View>

          <View style={styles.infoRow}>
            <Ionicons name="diamond-outline" size={14} color={FL.greenInk} style={styles.infoIcon} />
            <View>
              <Text style={styles.infoLabel}>Subscription</Text>
              <View style={[styles.planBadge, member.plan && member.plan !== "free" && styles.planBadgePaid]}>
                <Text style={[styles.planBadgeText, member.plan && member.plan !== "free" && { color: FL.onGreen }]}>{member.planName ?? "Free"}</Text>
              </View>
            </View>
          </View>
        </View>
      </Animated.View>
    </Modal>
  );
});

export default MemberCard;

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    position: "absolute",
    width: CARD_WIDTH,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: FL.hair2,
    overflow: "hidden",
    backgroundColor: FL.paper,
    ...(WEB ? { backgroundImage: "linear-gradient(rgba(255,255,255,0.07), rgba(255,255,255,0.07))", boxShadow: "0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(162,89,255,0.16)" } : { elevation: 8 }),
  },

  banner: {
    height: BANNER_HEIGHT,
    justifyContent: "flex-start",
    alignItems: "flex-end",
    padding: 10,
    backgroundColor: FL.greenWash,
    ...(WEB ? { backgroundImage: "linear-gradient(135deg, rgba(124,58,237,0.55) 0%, rgba(79,70,229,0.4) 55%, rgba(37,99,235,0.45) 100%)" } : {}),
  },
  bannerBar: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 1,
    backgroundColor: FL.hair2,
  },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 999,
    backgroundColor: "rgba(0,0,0,0.45)",
    alignItems: "center",
    justifyContent: "center",
  },

  avatarRow: {
    marginTop: -AVATAR_OVERLAP,
    paddingHorizontal: 14,
    marginBottom: 10,
  },
  avatarOuter: {
    width: AVATAR_SIZE + 4,
    height: AVATAR_SIZE + 4,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: FL.paper,
    backgroundColor: FL.paper,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: 999,
    backgroundColor: FL.green,
    ...(WEB ? { backgroundImage: ACCENT_GRADIENT } : {}),
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: {
    ...display(22, { width: 104 }),
    lineHeight: 26,
    color: FL.onGreen,
  },
  statusDot: {
    position: "absolute",
    bottom: -4,
    right: -4,
    width: 14,
    height: 14,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: FL.paper,
  },

  body: {
    paddingHorizontal: 14,
    paddingBottom: 14,
  },
  displayName: {
    fontFamily: FONT,
    fontSize: 18,
    fontWeight: "800",
    letterSpacing: -0.3,
    color: FL.ink,
  },
  handle: {
    fontFamily: FONT,
    fontSize: 13,
    color: FL.ink2,
    marginTop: 1,
  },
  statusRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 6,
  },
  statusPip: {
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  statusText: {
    ...label(11),
    color: FL.ink,
  },

  divider: {
    height: 1,
    backgroundColor: FL.hair,
    marginVertical: 12,
  },

  infoRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 10,
  },
  infoIcon: {
    marginTop: 1,
  },
  infoLabel: {
    ...label(10),
    marginBottom: 3,
  },
  infoValue: {
    fontFamily: FONT,
    fontSize: 13,
    color: FL.ink,
    fontWeight: "600",
  },

  planBadge: {
    alignSelf: "flex-start",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: FL.hair2,
  },
  planBadgePaid: {
    backgroundColor: FL.green,
    borderColor: "transparent",
    ...(WEB ? { backgroundImage: ACCENT_GRADIENT } : {}),
  },
  planBadgeText: {
    fontFamily: FONT,
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: FL.ink,
  },
});
